import { useMemo, useState } from "react";
import { CheckCircleOutlined, CloseCircleOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Badge, Button, Select, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { type StripeIntegration } from "@fitness-app/data-models/entities/ProductsSettings";

import ModalForm from "~/components/ModalForm/ModalForm";
import { useLoading } from "~/hooks/useLoading";

const ADD_NEW_ITEM = "ADD_NEW_ITEM";

const PaymentIntegration = ({ integrations }: { connected: boolean; integrations: StripeIntegration[] | null }) => {
  const { t } = useTranslation(["settings", "common"]);
  const [loading] = useLoading();
  const [showModal, toggleModal] = useState(false);
  const [selectedAccountName, changeSelectedAccount] = useState<undefined | string>(undefined);

  const selectedAccount = useMemo(() => {
    return integrations?.find((item) => item.name === selectedAccountName);
  }, [selectedAccountName, integrations]);

  // const createLinkForIntegration = async () => {
  //   toggleLoader(true);
  //   await dispatch(
  //     productsSettingsActions.generateLinkForIntegration({
  //       onFailure: () => void message.error("Fail when trying integrated with Stripe"),
  //       payload: undefined,
  //     }),
  //   );
  //   toggleLoader(false);
  // };

  const handleChange = (value?: string | undefined) => {
    if (value === ADD_NEW_ITEM || !value) {
      changeSelectedAccount(undefined);
      toggleModal(true);
      return;
    }
    changeSelectedAccount(value);
  };

  return (
    <Space direction="vertical" size={24}>
      <Typography.Title level={4}>{t("paymentGateway.stripe.connectedAccounts")}</Typography.Title>
      <Select
        size="large"
        placeholder={t("selectAccount")}
        style={{ minWidth: 600 }}
        notFoundContent={false}
        onChange={handleChange}
        onSelect={handleChange}
        value={selectedAccount?.name}
        optionFilterProp="title"
        optionLabelProp="title"
      >
        {(integrations || []).map((account) => (
          <Select.Option value={account.name} title={account.name} key={account.name}>
            {account.name}
          </Select.Option>
        ))}
        {
          <Select.Option disabled value={ADD_NEW_ITEM} title={t<string>("addNewAccount")}>
            <Button
              type="link"
              disabled
              style={{
                flex: "none",
                padding: "8px",
                display: "block",
                cursor: "pointer",
              }}
            >
              <PlusOutlined /> {t("addNewAccount")}
            </Button>
          </Select.Option>
        }
      </Select>
      {selectedAccount && (
        <>
          <Space direction="horizontal" align="end" style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button disabled type="dashed" icon={<EditOutlined />} onClick={() => toggleModal(true)}>
              {t("common:button.edit")}
            </Button>
          </Space>
          <div
            style={{
              maxWidth: 600,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Space size={16} direction="vertical" align="center">
              <Typography.Title level={5} style={{ marginBottom: 0 }}>
                {t<string>("settings:clientIntegration.status")}
              </Typography.Title>
              <Typography.Paragraph style={{ marginBottom: 0 }}>
                {t<string>("settings:paymentGateway.stripe.accountName")}: <strong>{selectedAccount.name}</strong>
              </Typography.Paragraph>
              <Typography.Paragraph style={{ marginBottom: 0 }}>
                {t<string>("settings:paymentGateway.stripe.accountId")}: <strong>{selectedAccount.accountId}</strong>
              </Typography.Paragraph>
              <Badge status="success" text={t<string>("settings:clientIntegration.completed")} />
              <Typography.Paragraph style={{ marginBottom: 0 }}>
                {t<string>("settings:paymentGateway.stripe.default")}:{" "}
                {selectedAccount.isDefault ? (
                  <CheckCircleOutlined className="text-green-500" />
                ) : (
                  <CloseCircleOutlined className="text-red-500" />
                )}
              </Typography.Paragraph>
            </Space>
          </div>
        </>
      )}

      {/* {!selectedAccount && (
        <Result
          status="info"
          title={t("integrateWithStripe.title")}
          subTitle={t("integrateWithStripe.desc")}
          extra={[
            <Button type="primary" key="button" loading={showLoader} onClick={createLinkForIntegration}>
              {t("integrateWithStripe.button")}
            </Button>,
            <div key="info" style={{ marginTop: 50, display: "flex", flexDirection: "column" }}>
              <h2>{t("integrateWithStripe.stripeFees")}</h2>
              <div>
                <img src={commission} alt="stripe-fees" style={{ maxWidth: 500, height: "auto" }} />
              </div>
              <a href="https://stripe.com/en-pl/pricing#pricing-details" target="_blank" rel="noreferrer">
                {t("integrateWithStripe.learnMore")}
              </a>
            </div>,
          ]}
        />
      )} */}

      <ModalForm
        open={showModal}
        loading={loading}
        onCancel={() => {
          toggleModal(false);
        }}
        title={selectedAccount ? t("paymentGateway.stripe.editAccount") : t("paymentGateway.stripe.addNewAccount")}
      >
        {/* TODO: Create StripeIntegrationForm component */}
        <div>Stripe Integration Form - TODO</div>
      </ModalForm>
    </Space>
  );
};

export default PaymentIntegration;
